import React, { useState, useEffect } from 'react';
import {
  Button, Modal, Form, App, Space, Card, Tabs
} from 'antd';
import { PlusOutlined, UploadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { FuelReceipt, Vehicle } from '../types';
import { fuelReceiptApi, vehicleApi } from '../services/api';
import { useLanguage } from '../contexts/LanguageContext';
import FuelReceiptFormFields from '../components/fuelReceipt/FuelReceiptFormFields';
import { getFuelReceiptColumns } from '../components/fuelReceipt/FuelReceiptTableColumns';
import { useFuelReceiptForm } from '../components/fuelReceipt/useFuelReceiptForm';
import { getFuelReceiptMobileColumns } from '../components/fuelReceipt/FuelReceiptMobileColumns';
import FuelEfficiencyCalculator from '../components/fuelReceipt/FuelEfficiencyCalculator';
import { FuelReceiptDraftsTab, FuelReceiptCreatedTab } from '../components/fuelReceipt/FuelReceiptTabs';
import FuelReceiptModalFooter from '../components/fuelReceipt/FuelReceiptModalFooter';
import FuelReceiptImageProcessingUpload from '../components/fuelReceipt/FuelReceiptImageProcessingUpload';
import FuelReceiptManualUpload from '../components/fuelReceipt/FuelReceiptManualUpload';
import { handleCalculateFuelEfficiency } from '../components/fuelReceipt/fuelEfficiencyCalculation';

const FuelReceiptPage: React.FC = () => {
  const { t } = useLanguage();
  const { message, modal } = App.useApp();
  const [, setFuelReceipts] = useState<FuelReceipt[]>([]);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingReceipt, setEditingReceipt] = useState<FuelReceipt | null>(null);
  const [form] = Form.useForm();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // New states for state management
  const [draftReceipts, setDraftReceipts] = useState<FuelReceipt[]>([]);
  const [createdReceipts, setCreatedReceipts] = useState<FuelReceipt[]>([]);
  const [activeTab, setActiveTab] = useState<'drafts' | 'created'>('drafts');
  const [modalType, setModalType] = useState<'image' | 'manual'>('manual');

  // Separate filter states for each tab
  const [draftLinkFilter, setDraftLinkFilter] = useState<'all' | 'linked' | 'unlinked'>('all');
  const [createdLinkFilter, setCreatedLinkFilter] = useState<'all' | 'linked' | 'unlinked'>('all');

  const fetchFuelReceipts = async () => {
    setLoading(true);
    try {
      const [allResponse, draftResponse, createdResponse] = await Promise.all([
        fuelReceiptApi.getAll(),
        fuelReceiptApi.getByState('WaitForProcess'),
        fuelReceiptApi.getByState('Created')
      ]);
      setFuelReceipts(allResponse.data);
      setDraftReceipts(draftResponse.data);
      setCreatedReceipts(createdResponse.data);
    } catch (error) {
      message.error('Error fetching fuel receipts');
    } finally {
      setLoading(false);
    }
  };

  // Use custom hook for form handling
  const {
    formData,
    receiptImage,
    processing,
    processResult,
    uploadError,
    uploadKey,
    setReceiptImage,
    setProcessResult,
    setUploadError,
    handleNumericChange,
    handleProcessImage,
    resetForm,
    loadReceiptForEdit,
    validateFile
  } = useFuelReceiptForm({ form, onSuccess: fetchFuelReceipts, t, vehicles });

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const fetchVehicles = async () => {
    try {
      const response = await vehicleApi.getAll();
      setVehicles(response.data);
    } catch (error) {
      console.error('Error fetching vehicles:', error);
    }
  };

  useEffect(() => {
    fetchFuelReceipts();
    fetchVehicles();
  }, [])

  const handleAdd = () => {
    setEditingReceipt(null);
    form.resetFields();
    form.setFieldsValue({
      refuelDate: dayjs()
    });
    resetForm();
    setModalType('manual');
    setModalVisible(true);
  };

  const handleAddByImage = () => {
    setEditingReceipt(null);
    form.resetFields();
    form.setFieldsValue({
      refuelDate: dayjs()
    });
    resetForm();
    setModalType('image');
    setModalVisible(true);
  };

  const handleEdit = (receipt: FuelReceipt) => {
    setEditingReceipt(receipt);
    loadReceiptForEdit(receipt);
    setModalType(receipt.receiptImagePath ? 'image' : 'manual');
    setModalVisible(true);
  };

  const handleDelete = async (id: number) => {
    try {
      await fuelReceiptApi.delete(id);
      message.success('Fuel receipt deleted successfully');
      fetchFuelReceipts();
    } catch (error: any) {
      message.error(error?.message || 'Failed to delete fuel receipt');
    }
  };

  const handleUnlink = async (id: number) => {
    try {
      await fuelReceiptApi.unlink(id);
      message.success('Fuel receipt unlinked successfully');
      fetchFuelReceipts();
    } catch (error: any) {
      message.error(error?.message || 'Failed to unlink fuel receipt');
    }
  };

  const handleStateTransition = async (id: number, newState: 'WaitForProcess' | 'Created') => {
    // If finalizing, check for duplicate receipt number first
    if (newState === 'Created') {
      const receipt = [...draftReceipts, ...createdReceipts].find(r => r.id === id);
      if (receipt?.receiptNumber) {
        try {
          const checkResponse = await fuelReceiptApi.checkDuplicateReceipt(receipt.receiptNumber, id);
          if (checkResponse.data.isDuplicate) {
            const existing = checkResponse.data.existingReceipt;
            modal.confirm({
              title: (
                <span style={{ color: '#cf1322', fontSize: 16 }}>
                  ⚠️ {t('duplicate.receipt.warning') || 'Duplicate Receipt Number'}
                </span>
              ),
              icon: null,
              width: 480,
              content: (
                <div style={{ padding: '12px 0' }}>
                  <div style={{ 
                    background: '#fff2f0', 
                    border: '1px solid #ffccc7', 
                    borderRadius: 8, 
                    padding: 16, 
                    marginBottom: 16 
                  }}>
                    <div style={{ fontWeight: 600, marginBottom: 8, color: '#434343' }}>
                      {t('receipt.number.already.exists') || 'Receipt number'}: <span style={{ color: '#cf1322' }}>"{receipt.receiptNumber}"</span>
                    </div>
                    <div style={{ color: '#595959' }}>
                      {t('already.exists.in.database') || 'already exists in the database'}
                    </div>
                  </div>
                  {existing && (
                    <div style={{ 
                      background: '#fafafa', 
                      border: '1px solid #d9d9d9', 
                      borderRadius: 8, 
                      padding: 12, 
                      marginBottom: 16,
                      fontSize: 13 
                    }}>
                      <div style={{ fontWeight: 600, marginBottom: 6, color: '#434343' }}>
                        {t('existing.record') || 'Existing Record'}:
                      </div>
                      <div style={{ color: '#595959', lineHeight: 1.8 }}>
                        <div>ID: <strong>{existing.id}</strong></div>
                        {existing.gasStation && <div>{t('gas.station') || 'Gas Station'}: <strong>{existing.gasStation}</strong></div>}
                        {existing.totalAmount != null && <div>{t('total.amount') || 'Total'}: <strong>{existing.totalAmount.toLocaleString()}₫</strong></div>}
                      </div>
                    </div>
                  )}
                  <div style={{ color: '#595959' }}>
                    {t('continue.anyway') || 'Do you want to continue?'}
                  </div>
                </div>
              ),
              okText: t('continue') || 'Continue',
              okButtonProps: { danger: true },
              cancelText: t('cancel') || 'Cancel',
              onOk: async () => {
                await performStateTransition(id, newState);
              }
            });
            return;
          }
        } catch (error) {
          // If duplicate check fails, proceed with the transition
          console.error('Error checking duplicate receipt:', error);
        }
      }
    }
    await performStateTransition(id, newState);
  };

  const performStateTransition = async (id: number, newState: 'WaitForProcess' | 'Created') => {
    try {
      const response = await fuelReceiptApi.updateState(id, newState);
      // Check if the response contains a duplicate warning
      if (response.data?.warning) {
        message.warning(response.data.warning);
      }
   message.success(`Receipt ${newState === 'Created' ? 'finalized' : 'moved to drafts'} successfully`);
      fetchFuelReceipts();
    } catch (error: any) {
      message.error(error?.message || 'Failed to update receipt state');
    }
  };

  const handleSaveAsDraft = async () => {
    try {
      const values = await form.getFieldsValue();
      const formData_obj = new FormData();
      
      if (values.vehicleId) formData_obj.append('vehicleId', values.vehicleId.toString());
      
      if (values.refuelDate) {
        const localDateTime = values.refuelDate.format('YYYY-MM-DDTHH:mm:ss');
        formData_obj.append('refuelDate', localDateTime);
      }
      
      if (formData.liters) formData_obj.append('liters', formData.liters.toString());
      if (formData.pricePerLiter) formData_obj.append('pricePerLiter', formData.pricePerLiter.toString());
      if (formData.totalAmount) formData_obj.append('totalAmount', formData.totalAmount.toString());
      if (values.receiptNumber) formData_obj.append('receiptNumber', values.receiptNumber);
      if (values.gasStation) formData_obj.append('gasStation', values.gasStation);
      if (values.location) formData_obj.append('location', values.location);
      if (formData.odometer) formData_obj.append('odometer', formData.odometer.toString());
      if (values.notes) formData_obj.append('notes', values.notes);
      
      formData_obj.append('state', 'WaitForProcess');
      
      if (receiptImage) {
        formData_obj.append('receiptImageFile', receiptImage);
      }

      if (editingReceipt) {
        await fuelReceiptApi.update(editingReceipt.id, formData_obj);
        message.success(t('fuel.receipt.saved.as.draft') || 'Fuel receipt saved as draft');
      } else {
        await fuelReceiptApi.create(formData_obj);
        message.success(t('fuel.receipt.saved.as.draft') || 'Fuel receipt saved as draft');
      }
      
      handleCloseModal();
      fetchFuelReceipts();
    } catch (error: any) {
      handleApiError(error);
    }
  };

  const handleCreate = async () => {
    try {
      const values = await form.validateFields();
      
      if (formData.liters <= 0) {
        message.error(t('please.enter.valid.liters' || 'Please enter valid liters'));
        return;
      }
      if (formData.pricePerLiter <= 0) {
        message.error(t('please.enter.valid.price') || 'Please enter valid price per liter');
        return;
      }
      if (formData.totalAmount <= 0) {
        message.error(t('please.enter.valid.total' || 'Please enter valid total amount'));
        return;
      }

      const formData_obj = new FormData();
      
      formData_obj.append('vehicleId', values.vehicleId.toString());
      
      const localDateTime = values.refuelDate.format('YYYY-MM-DDTHH:mm:ss');
      formData_obj.append('refuelDate', localDateTime);
      
      formData_obj.append('liters', formData.liters.toString());
      formData_obj.append('pricePerLiter', formData.pricePerLiter.toString());
      formData_obj.append('totalAmount', formData.totalAmount.toString());
      
      if (values.receiptNumber) formData_obj.append('receiptNumber', values.receiptNumber);
      if (values.gasStation) formData_obj.append('gasStation', values.gasStation);
      if (values.location) formData_obj.append('location', values.location);
      if (formData.odometer > 0) formData_obj.append('odometer', formData.odometer.toString());
      if (values.notes) formData_obj.append('notes', values.notes);
      
      formData_obj.append('state', 'Created');
      
      if (receiptImage) {
        formData_obj.append('receiptImageFile', receiptImage);
      }

      if (editingReceipt) {
        await fuelReceiptApi.update(editingReceipt.id, formData_obj);
        message.success(t('fuel.receipt.updated') || 'Fuel receipt updated successfully');
      } else {
        await fuelReceiptApi.create(formData_obj);
        message.success(t('fuel.receipt.created') || 'Fuel receipt created successfully');
      }
      
      handleCloseModal();
      fetchFuelReceipts();
    } catch (error: any) {
      handleApiError(error);
    }
  };

  const handleViewImage = async (id: number) => {
    try {
      const response = await fuelReceiptApi.getImageBlob(id);
      const blob = new Blob([response.data], { type: response.headers['content-type'] });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => window.URL.revokeObjectURL(url), 10000);
    } catch (error) {
      message.error(t('failed.to.load.image') || 'Failed to load image');
    }
  };

  const handleApiError = (error: any) => {
    console.error(error);
    if (error?.response?.data?.message) {
      message.error(error.response.data.message);
    } else if (error?.message) {
      message.error(error.message);
    } else {
      message.error(t('error.saving.fuel.receipt') || 'Error saving fuel receipt');
    }
  };

  const handleCloseModal = () => {
    setModalVisible(false);
    resetForm();
  };

  const handleCalculateEfficiency = async (vehicleId: number) => {
    await handleCalculateFuelEfficiency({
      vehicleId,
      t,
      isMobile,
      onSuccess: fetchFuelReceipts,
      setLoading
    });
  };

  // Get table columns
  const columns = getFuelReceiptColumns({
    t,
    onEdit: handleEdit,
    onDelete: handleDelete,
    onUnlink: handleUnlink,
    onStateTransition: handleStateTransition,
    onViewImage: handleViewImage
  });

  // Mobile-optimized columns
  const mobileColumns = getFuelReceiptMobileColumns({
    t,
    onEdit: handleEdit,
    onDelete: handleDelete,
    onUnlink: handleUnlink,
    onStateTransition: handleStateTransition,
    onViewImage: handleViewImage
  });

  return (
    <div style={{ padding: isMobile ? '12px' : '24px' }}>
    <Card bodyStyle={{ padding: isMobile ? '12px' : '24px' }}>
        {/* Header - Responsive */}
        <div style={{ 
     marginBottom: '16px', 
        display: 'flex', 
        flexDirection: isMobile ? 'column' : 'row',
          justifyContent: 'space-between', 
          alignItems: isMobile ? 'stretch' : 'center',
    gap: isMobile ? '12px' : '0'
     }}>
          <h2 style={{ margin: 0, fontSize: isMobile ? '18px' : '24px' }}>{t('fuel.receipt.management')}</h2>
     <Space wrap style={{ justifyContent: isMobile ? 'center' : 'flex-end' }}>
  <Button
              type="primary"
              icon={<UploadOutlined />}
              onClick={handleAddByImage}
   size={isMobile ? 'middle' : 'middle'}
            >
       {isMobile ? t('by.image') || 'Image' : t('add.by.image') || 'Add by Image'}
            </Button>
            <Button
              type="default"
       icon={<PlusOutlined />}
        onClick={handleAdd}
              size={isMobile ? 'middle' : 'middle'}
            >
      {isMobile ? t('manual') || 'Manual' : t('add.manually') || 'Add Manually'}
         </Button>
 </Space>
        </div>

        {/* Fuel Efficiency Calculator - Responsive */}
        <FuelEfficiencyCalculator
          t={t}
          vehicles={vehicles}
          isMobile={isMobile}
          onCalculateEfficiency={handleCalculateEfficiency}
        />

      <Tabs 
          activeKey={activeTab}
          onChange={(key) => setActiveTab(key as 'drafts' | 'created')}
          size={isMobile ? 'small' : 'middle'}
     items={[
            {
      key: 'drafts',
       label: `${isMobile ? 'Drafts' : t('draft.receipts') || 'Draft Receipts'} (${draftReceipts.length})`,
              children: (
                <FuelReceiptDraftsTab
                  t={t}
                  isMobile={isMobile}
                  draftReceipts={draftReceipts}
                  draftLinkFilter={draftLinkFilter}
                  onFilterChange={setDraftLinkFilter}
                  mobileColumns={mobileColumns}
                  desktopColumns={columns}
                  loading={loading}
                />
              )
            },
     {
          key: 'created',
         label: `${isMobile ? 'Created' : t('created.receipts') || 'Created Receipts'} (${createdReceipts.length})`,
              children: (
                <FuelReceiptCreatedTab
                  t={t}
                  isMobile={isMobile}
                  createdReceipts={createdReceipts}
                  createdLinkFilter={createdLinkFilter}
                  onFilterChange={setCreatedLinkFilter}
                  mobileColumns={mobileColumns}
                  desktopColumns={columns}
                  loading={loading}
                />
              )
            }
          ]}
      />

<Modal
  title={editingReceipt 
            ? t('edit.fuel.receipt') 
    : modalType === 'image' 
           ? (t('add.by.image') || 'Add by Image')
           : (t('add.manually') || 'Add Manually')
       }
          open={modalVisible}
          onCancel={handleCloseModal}
          footer={
            <FuelReceiptModalFooter
              t={t}
              isMobile={isMobile}
              editingReceipt={editingReceipt}
              onCancel={handleCloseModal}
              onSaveAsDraft={handleSaveAsDraft}
              onCreate={handleCreate}
            />
          }
          width={isMobile ? '95%' : 800}
          style={{ top: isMobile ? 20 : undefined }}
     bodyStyle={{ maxHeight: isMobile ? '70vh' : undefined, overflowY: 'auto' }}
  >
   {/* Fuel Receipt Image Upload & Process Section */}
          {modalType === 'image' && (
            <FuelReceiptImageProcessingUpload
              t={t}
              isMobile={isMobile}
              uploadKey={uploadKey}
              receiptImage={receiptImage}
              processing={processing}
              processResult={processResult}
              uploadError={uploadError}
              editingReceipt={editingReceipt}
              onImageChange={setReceiptImage}
              onProcessResult={setProcessResult}
              onUploadError={setUploadError}
              onEditingReceiptUpdate={setEditingReceipt}
              onFetchReceipts={fetchFuelReceipts}
              onProcessImage={handleProcessImage}
              validateFile={validateFile}
            />
          )}

          {/* Simple File Upload for Manual Mode */}
          {modalType === 'manual' && (
            <FuelReceiptManualUpload
              t={t}
              isMobile={isMobile}
              uploadKey={uploadKey}
              receiptImage={receiptImage}
              editingReceipt={editingReceipt}
              onImageChange={setReceiptImage}
              onEditingReceiptUpdate={setEditingReceipt}
              onFetchReceipts={fetchFuelReceipts}
              validateFile={validateFile}
            />
          )}

       <Form
         form={form}
   layout={isMobile ? 'vertical' : 'horizontal'}
  labelCol={isMobile ? undefined : { span: 6 }}
      wrapperCol={isMobile ? undefined : { span: 18 }}
 >
            <FuelReceiptFormFields
              vehicles={vehicles}
              formData={formData}
              onNumericChange={handleNumericChange}
              isMobile={isMobile}
            />
          </Form>
 </Modal>
      </Card>
    </div>
  );
};

export default FuelReceiptPage;